import {Stack, Typography, Button} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import ShareButtons from '../../../../components/buttons/ShareButtons';

export const ResultsFooter = ({
  organization,
  features,
  item,
  cardRef,
  handleUpgradeToSeeResults,
  config,
  currentSlideKey,
  headerData,
  sortedMetricsByHealthOrder, // Add this prop for products slide
  onNavigateToProducts, // Add this prop for health score navigation
  transformedHealthMetrics, // Add this prop to get full transformed data
}) => {
  const navigate = useNavigate();

  console.log('ResultsFooter rendered with:');
  console.log('- currentSlideKey:', currentSlideKey);
  console.log('- transformedHealthMetrics:', transformedHealthMetrics);
  console.log('- sortedMetricsByHealthOrder:', sortedMetricsByHealthOrder);

  const shouldBlur = features.blurCategories?.includes(item?.name) && !item?.isShowAllResultsCard;

  // Auto-detect if this is a health score slide
  const isHealthScoreSlide =
    currentSlideKey === 'ALL_HEALTH' ||
    headerData?.name === 'Overall Health Score' ||
    headerData?.name === 'Health Score';

  // Check if this is a products slide
  const isProductsSlide =
    currentSlideKey === 'PRODUCTS' ||
    currentSlideKey === 'products' || // Add lowercase check
    headerData?.name === 'Top Products for You';

  // Check if this is a lab test slide (HelloHealth specific)
  const isLabTestSlide =
    currentSlideKey === 'LAB_TEST' ||
    currentSlideKey === 'lab_test' || // Add lowercase check
    headerData?.name === 'Recommended Lab Tests' ||
    headerData?.name === 'Lab Tests For You';

  // Handle navigation to products slide for health score cards
  const handleHealthScoreProductsNavigation = () => {
    console.log('Health Score - Find Products button clicked');

    // Navigate to products slide within the same page
    if (onNavigateToProducts) {
      onNavigateToProducts();
    } else {
      console.warn('onNavigateToProducts handler not provided');
    }
  };

  // Handle Learn More navigation for products slide
  const handleLearnMoreNavigation = () => {
    console.log('Products Learn More clicked from footer');
    console.log('transformedHealthMetrics:', transformedHealthMetrics);
    console.log('sortedMetricsByHealthOrder:', sortedMetricsByHealthOrder);

    // First try to use the full transformed health metrics
    if (transformedHealthMetrics?.physicalHealth) {
      const physicalHealthItem = transformedHealthMetrics.physicalHealth;
      console.log('Using transformed Physical Health data:', physicalHealthItem);

      // Create analysis item with full transformed data
      const analysisItem = {
        ...physicalHealthItem, // This contains name, value, rating, results, etc.
        isGameCompleted: true,
        userAssessmentID: localStorage.getItem('session-id') || 'default',
        cardKey: 'physicalHealth',
        category: physicalHealthItem.name,
        healthName: physicalHealthItem.name,
        fieldValue: physicalHealthItem.value,
        healthOrderNo: physicalHealthItem.value,
      };

      console.log('Final analysis item with transformed data:', analysisItem);

      navigate('/results/analysis', {
        state: {
          item: analysisItem,
        },
      });
      return;
    }

    console.log('Transformed data not available, using fallback...');

    // Fallback to sortedMetricsByHealthOrder if transformed data not available
    if (!sortedMetricsByHealthOrder || !Array.isArray(sortedMetricsByHealthOrder)) {
      console.warn('No valid health metrics data available');
      navigate('/');
      return;
    }

    // Find Physical Health item from sortedMetricsByHealthOrder
    const physicalHealthItem = sortedMetricsByHealthOrder.find(
      item =>
        item.healthName === 'Physical Health' ||
        item.category === 'Physical Health' ||
        item.cardKey === 'physicalHealth' ||
        item.healthName?.toLowerCase().includes('physical'),
    );

    console.log('Found Physical Health item from sorted metrics:', physicalHealthItem);

    if (physicalHealthItem) {
      // Try to get full transformed data for this item
      const fullTransformedItem = transformedHealthMetrics?.[physicalHealthItem.cardKey];
      console.log('Full transformed item for cardKey:', physicalHealthItem.cardKey, fullTransformedItem);

      const analysisItem = {
        name: physicalHealthItem.healthName || physicalHealthItem.category,
        value: physicalHealthItem.fieldValue,
        rating: physicalHealthItem.fieldValue,
        cardKey: physicalHealthItem.cardKey,
        category: physicalHealthItem.category,
        healthName: physicalHealthItem.healthName,
        fieldValue: physicalHealthItem.fieldValue,
        healthOrderNo: physicalHealthItem.healthOrderNo,
        isGameCompleted: true,
        userAssessmentID: localStorage.getItem('session-id') || 'default',
        // Use transformed results if available, otherwise empty array
        results: fullTransformedItem?.results || [],
        resultHeading: fullTransformedItem?.resultHeading || `Your ${physicalHealthItem.healthName} Metrics`,
      };

      console.log('Final analysis item with fallback data:', analysisItem);

      navigate('/results/analysis', {
        state: {
          item: analysisItem,
        },
      });
    } else {
      console.log('Physical Health not found, using first item...');
      // Fallback: use the first item
      const firstItem = sortedMetricsByHealthOrder[0];
      if (firstItem) {
        const fullTransformedItem = transformedHealthMetrics?.[firstItem.cardKey];
        console.log('Using first item:', firstItem, 'with transformed data:', fullTransformedItem);

        const analysisItem = {
          name: firstItem.healthName || firstItem.category,
          value: firstItem.fieldValue,
          rating: firstItem.fieldValue,
          cardKey: firstItem.cardKey,
          category: firstItem.category,
          healthName: firstItem.healthName,
          fieldValue: firstItem.fieldValue,
          healthOrderNo: firstItem.healthOrderNo,
          isGameCompleted: true,
          userAssessmentID: localStorage.getItem('session-id') || 'default',
          results: fullTransformedItem?.results || [],
          resultHeading: fullTransformedItem?.resultHeading || `Your ${firstItem.healthName} Metrics`,
        };

        console.log('Final analysis item with first item:', analysisItem);

        navigate('/results/analysis', {
          state: {
            item: analysisItem,
          },
        });
      } else {
        console.warn('No items found in sortedMetricsByHealthOrder');
        navigate('/');
      }
    }
  };

  // Default organization blur handling
  if (organization.type === 'DEFAULT' && shouldBlur) {
    return (
      <Stack alignItems="center" spacing={2}>
        <Typography sx={{color: 'rgba(0, 0, 0, 0.69)', fontSize: '14px', fontWeight: 600}}>$2.99 per week</Typography>
        <Button
          fullWidth
          onClick={handleUpgradeToSeeResults}
          sx={{
            height: '60px',
            borderRadius: '40px',
            background: config.button?.background,
            color: '#fff',
            fontSize: '18px',
            fontWeight: 600,
            maxWidth: '400px',
            '&:hover': {opacity: 0.9},
          }}
        >
          🚀 Upgrade to See Results
        </Button>
        <Typography sx={{color: '#35C6C9', fontSize: '20px', fontWeight: 600, textAlign: 'center'}}>
          I'd Rather Invite Friends to View My Results... 👯‍♀️
        </Typography>
      </Stack>
    );
  }

  // Health score slide - now includes the Find Products button
  if (isHealthScoreSlide) {
    return (
      <Stack alignItems="center" spacing={2}>
        <Button
          fullWidth
          onClick={handleHealthScoreProductsNavigation}
          sx={{
            height: '60px',
            borderRadius: organization.flags?.isAtpl ? '10px' : '40px',
            background: config.button?.background,
            color: '#fff',
            fontSize: '18px',
            fontWeight: 600,
            maxWidth: '400px',
            '&:hover': {opacity: 0.9},
          }}
        >
          👀 Find Products That Can Help
        </Button>
        {features.showShareButtons && (
          <ShareButtons cardRef={cardRef} item={item} socialButtonsCss={config?.socialButtons} />
        )}
        <Typography variant="caption" sx={{textAlign: 'center', color: 'text.secondary', maxWidth: '800px'}}>
          These numbers are only estimates and are not a substitute for the judgment of a healthcare professional. They
          are intended to improve awareness and general wellness.
        </Typography>
      </Stack>
    );
  }

  // Lab test slide - special handling with See All Tests button (HelloHealth specific)
  if (isLabTestSlide) {
    return (
      <Stack alignItems="center" spacing={2}>
        <Button
          fullWidth
          onClick={() => window.open('https://www.gethellohealth.com/collections/lab-tests-1', '_blank')}
          sx={{
            height: '60px',
            borderRadius: organization.flags?.isAtpl ? '10px' : '40px',
            background: config.button?.background,
            color: '#fff',
            fontSize: '18px',
            fontWeight: 600,
            maxWidth: '400px',
            '&:hover': {opacity: 0.9},
          }}
        >
          🧪 See All Tests
        </Button>
        {features.showShareButtons && (
          <ShareButtons cardRef={cardRef} item={item} socialButtonsCss={config?.socialButtons} />
        )}
        <Typography variant="caption" sx={{textAlign: 'center', color: 'text.secondary', maxWidth: '800px'}}>
          These numbers are only estimates and are not a substitute for the judgment of a healthcare professional. They
          are intended to improve awareness and general wellness.
        </Typography>
      </Stack>
    );
  }
  // Products slide - special handling with Learn More button
  if (isProductsSlide) {
    return (
      <Stack alignItems="center" spacing={2}>
        <Button
          fullWidth
          onClick={handleLearnMoreNavigation}
          sx={{
            height: '60px',
            borderRadius: organization.flags?.isAtpl ? '10px' : '40px',
            background: config.button?.background,
            color: '#fff',
            fontSize: '18px',
            fontWeight: 600,
            maxWidth: '400px',
            '&:hover': {opacity: 0.9},
          }}
        >
          Learn More
        </Button>
        {features.showShareButtons && (
          <ShareButtons cardRef={cardRef} item={item} socialButtonsCss={config?.socialButtons} />
        )}
        <Typography variant="caption" sx={{textAlign: 'center', color: 'text.secondary', maxWidth: '800px'}}>
          These numbers are only estimates and are not a substitute for the judgment of a healthcare professional. They
          are intended to improve awareness and general wellness.
        </Typography>
      </Stack>
    );
  }

  // Normal footer with action button and share buttons (for non-health score, non-products slides)
  return (
    <Stack alignItems="center" spacing={2}>
      <Link to="/results/analysis" state={{item}} style={{textDecoration: 'none', width: '100%', maxWidth: '400px'}}>
        <Button
          fullWidth
          sx={{
            height: '60px',
            borderRadius: organization.flags?.isAtpl ? '10px' : '40px',
            background: config.button?.background,
            color: '#fff',
            fontSize: '18px',
            fontWeight: 600,
            '&:hover': {opacity: 0.9},
          }}
        >
          👀 Find Products That Can Help
        </Button>
      </Link>
      {features.showShareButtons && (
        <ShareButtons cardRef={cardRef} item={item} socialButtonsCss={config?.socialButtons} />
      )}
      <Typography variant="caption" sx={{textAlign: 'center', color: 'text.secondary', maxWidth: '800px'}}>
        These numbers are only estimates and are not a substitute for the judgment of a healthcare professional. They
        are intended to improve awareness and general wellness.
      </Typography>
    </Stack>
  );
};
